/* The Raw tab: the source payload exactly as the ERP or the ASP sent it.
 *
 * Input:  docId, and the access reason already given (sent as X-Access-Reason; blank for none).
 * Output: the byte count, then the payload formatted for reading - JSON indented, XML
 *         tokenised - with a toggle back to the original bytes. Anything else shows as sent.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { Notice, XmlView } from "../../../design/components";
import { useDocumentRaw } from "../../../api/queries";
import styles from "./DocumentInspector.module.css";

type Formatted = { kind: "json" | "xml" | "text"; text: string };

function byteCount(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** Input: the payload text. Output: its kind and, for JSON, the indented form. */
export function formatPayload(raw: string): Formatted {
  if (raw.trimStart().startsWith("<")) return { kind: "xml", text: raw };
  try {
    return { kind: "json", text: JSON.stringify(JSON.parse(raw), null, 2) };
  } catch {
    return { kind: "text", text: raw };
  }
}

export function RawPane({ docId, reason }: { docId: string; reason: string }) {
  const { t } = useTranslation();
  const raw = useDocumentRaw(docId, reason || undefined);
  const [original, setOriginal] = useState(false);
  const view = useMemo(() => (raw.data === undefined ? null : formatPayload(raw.data)), [raw.data]);

  if (raw.isError) {
    return <Notice kind="fail">{t("documents.inspector.raw.loadError", "The source payload could not be loaded.")}</Notice>;
  }
  if (raw.data === undefined || view === null) return <p className={styles.sub}>{t("documents.inspector.raw.loading", "Loading…")}</p>;

  const formatted = !original && view.kind !== "text";
  return (
    <>
      <div className={styles.paneHead}>
        <span className={styles.sub}>
          {t("documents.inspector.raw.byteCount", "{{count}} bytes", { count: byteCount(raw.data) })}
        </span>
        {view.kind !== "text" ? (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            aria-pressed={original}
            onClick={() => setOriginal((shown) => !shown)}
          >
            {original
              ? t("documents.inspector.raw.showFormatted", "Show formatted")
              : t("documents.inspector.raw.showOriginal", "Show original")}
          </button>
        ) : null}
      </div>
      {formatted && view.kind === "xml" ? (
        <div className={styles.pane}>
          <XmlView src={raw.data} />
        </div>
      ) : (
        <pre className={`code ${styles.pane}${formatted ? ` ${styles.wrapped}` : ""}`}>
          {formatted ? view.text : raw.data}
        </pre>
      )}
    </>
  );
}
